import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { DATA_TYPE_TO_EVENT_VERSION } from "components/saved_searches/utils";
import { graphql } from "lib/generated/app-service-gql";
import {
  CreateSavedViewMutation,
  CreateSavedViewMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { createSavedViewCreatedEvent } from "lib/savedViewTracking";
import { EventNames, useTracking } from "lib/tracking";
import { useGraphQlMutation } from "../useGraphQlClient";

const createSavedViewMutation = graphql(`
  mutation CreateSavedView($request: CreateSavedViewRequest!) {
    createSavedView(CreateSavedViewRequest: $request) {
      id
      name
      description
      dataType
      createdAt
    }
  }
`);

export function useCreateSavedView(
  options?: UseMutationOptions<
    CreateSavedViewMutation,
    unknown,
    CreateSavedViewMutationVariables,
    unknown
  >,
) {
  const queryClient = useQueryClient();
  const { logEvent } = useTracking();

  const { data, ...rest } = useGraphQlMutation(createSavedViewMutation, {
    ...options,
    onSuccess: async (data, variables, context) => {
      // Await refetch so the new view is in the cache before the consumer's onSuccess
      // runs (e.g. setSelectedView). Without this, setSelectedView clears URL filters
      // and filterValue briefly falls back to the previous view's filters, causing a
      // flash of the old filter count.
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ["savedViews"] }),
        queryClient.refetchQueries({ queryKey: ["views"] }),
      ]);

      void queryClient.invalidateQueries({ queryKey: ["leadSubscriptionContents"] });
      void queryClient.invalidateQueries({ queryKey: ["leadSubscriptions"] });

      logEvent(
        EventNames.viewCreated,
        createSavedViewCreatedEvent(
          data.createSavedView.name,
          DATA_TYPE_TO_EVENT_VERSION[data.createSavedView.dataType],
          variables.request.alertFrequency || "NEVER",
          data.createSavedView.id,
        ),
      );

      options?.onSuccess?.(data, variables, context);
    },
  });

  return { data: data?.createSavedView, ...rest };
}
